import { OnInit, Component, inject } from '@angular/core';
import { ListService, PermissionService } from '@abp/ng.core';
import { CardBodyComponent, CardComponent, ConfirmationService } from '@abp/ng.theme.shared';
import { BaseCrudComponent, CrudPage, IActionsSchema, ICrudService, resolveActions } from '@zendra/shared';
import {
  GetGisStandAreaDifferenceProjectionListInputDto,
  GisStandAreaDifferencePlanningOrchestratorService,
  GisStandAreaDifferenceProjectionDto,
  GisStandAreaDifferenceProjectionExcelDownloadDto,
} from 'orchestrator';
import { map, Observable, throwError } from 'rxjs';
import {
  GISSTANDAREADIFFERENCE_CONFIG,
  GISSTANDAREADIFFERENCE_FIELDS,
  GISSTANDAREADIFFERENCE_TABLE_OPTIONS,
  STAND_AREA_DIFFERENCE_THRESHOLD,
} from './gis-stand-area-difference.config';

type GisStandAreaDifferenceRow = GisStandAreaDifferenceProjectionDto & { id: number };

type UnsupportedCrudDto = Record<string, never>;

@Component({
  selector: 'app-gis-stand-area-difference',
  imports: [CardComponent, CardBodyComponent, CrudPage],
  providers: [ListService, ConfirmationService],
  templateUrl: './gis-stand-area-difference.html',
})
export class GisStandAreaDifference extends BaseCrudComponent<
  GisStandAreaDifferenceRow,
  UnsupportedCrudDto,
  UnsupportedCrudDto,
  GisStandAreaDifferenceProjectionExcelDownloadDto
> implements OnInit {
  private readonly gisStandAreaDifferenceService = inject(
    GisStandAreaDifferencePlanningOrchestratorService,
  );
  private readonly permissionService = inject(PermissionService);

  readonly config = GISSTANDAREADIFFERENCE_CONFIG;
  readonly fields = GISSTANDAREADIFFERENCE_FIELDS;
  readonly tableOptions = GISSTANDAREADIFFERENCE_TABLE_OPTIONS;
  actions: IActionsSchema | null = null;

  ngOnInit(): void {
    if (!this.permissionService.getGrantedPolicy('PlanningGeography.StandAreaMaintenances.Create')) {
      this.actions = {
        overTable: [],
        multipleRecord: [],
        singleRecord: [],
      };
      return;
    }

    this.actions = this.withGridRefreshOnActionCompleted(
      resolveActions(this.config.actions, {}),
    );
  }

  /**
   * Fixed filter of the query. The projection holds every stand, so this is what narrows it down
   * to the US scope. It is spread after the grid query so the user cannot clear it from the
   * generic filter row, the same way the other GIS inconsistency views pin their case.
   */
  private get areaDifferenceFilter(): Partial<GetGisStandAreaDifferenceProjectionListInputDto> {
    return {
      isActive: true,
      areaDifferenceGreaterThan: STAND_AREA_DIFFERENCE_THRESHOLD,
    };
  }

  service: ICrudService<
    GisStandAreaDifferenceRow,
    UnsupportedCrudDto,
    UnsupportedCrudDto,
    GisStandAreaDifferenceProjectionExcelDownloadDto
  > = {
    create: (_input: UnsupportedCrudDto) => this.unsupportedCrudOperation('create'),
    update: (_id: number, _input: UnsupportedCrudDto) => this.unsupportedCrudOperation('update'),
    deleteByIds: (_ids: number[]) => this.unsupportedCrudOperation('delete'),
    getList: query =>
      this.gisStandAreaDifferenceService
        .getList({
          ...query,
          ...this.areaDifferenceFilter,
        } as GetGisStandAreaDifferenceProjectionListInputDto)
        .pipe(
          map(response => ({
            items: (response.items ?? []) as GisStandAreaDifferenceRow[],
            totalCount: response.totalCount ?? 0,
          })),
        ),
    getDownloadToken: () => this.gisStandAreaDifferenceService.getDownloadToken(),
    getListAsExcelFile: input =>
      this.gisStandAreaDifferenceService.getListAsExcelFile({
        ...input,
        ...this.areaDifferenceFilter,
      } as GisStandAreaDifferenceProjectionExcelDownloadDto),
  };

  protected prepareUpdateDto(
    _data: any,
    _selected: GisStandAreaDifferenceRow,
  ): UnsupportedCrudDto {
    return {};
  }

  protected getEntityName(): string {
    return 'GisStandAreaDifference';
  }

  private unsupportedCrudOperation<T>(operation: string): Observable<T> {
    return throwError(
      () =>
        new Error(
          `Stands with area difference is a read-only projection-backed report, so ${operation} is not supported.`,
        ),
    );
  }
}
